
import { computed, defineComponent, PropType } from 'vue'
import { FreeForm } from '@/modules/free-forms/types'
import { useAPIPagination } from '@/views/_shared/search-list/pagination'
import { FormModelCacheTag } from '@/modules/_shared/forms'
import { FreeFormsRouteNames } from '@/router/routes-names'
import { useRouter, useRoute } from 'vue-router'
import { useFreeFormsModule } from '@/modules/free-forms'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import Uuid from '@/components/_shared/uuid.vue'

export default defineComponent({
    name: 'free-forms-list',
    components: { Uuid },
    props: {
        freeForms: {
            type: Array as PropType<FreeForm[]>,
            required: true
        },
        filter: {
            type: String
        }
    },
    async setup (props) {
        const $q = useQuasar()
        const router = useRouter()
        const route = useRoute()
        const { getFreeForms, freeFormsFilter, freeFormsCount, enableFreeForm, disableFreeForm } = useFreeFormsModule()

        const freeFormsColumns = ([
            { name: 'id', label: 'Form ID', field: 'id', sortable: true, align: 'left' },
            { name: 'name', label: 'Name', field: 'name', sortable: true, align: 'left' },
            { name: 'slug', label: 'Slug', field: 'slug', sortable: true, align: 'left' },
            { name: 'subscriptionType', label: 'Subscription type', field: 'subscriptionType', sortable: true, align: 'left' },
            { name: 'processType', label: 'Process type', field: 'processType', sortable: true, align: 'left' },
            { name: 'accessAfterDays', label: 'Access after', field: (row: FreeForm) => row.accessAfterDays + ' days', sortable: true, align: 'left' },
            { name: 'accessDuration', label: 'Access duration', field: (row: FreeForm) => row.accessDuration + ' days', sortable: true, align: 'left' },
            { name: 'areTranslationsReady', label: 'Translation status', field: (row: FreeForm) => row.areTranslationsReady ? 'Ready' : 'Not ready', sortable: true, align: 'left' },
            { name: 'actions', label: 'Actions', field: 'actions', align: 'left' }
        ])

        const currentPage = computed(() => route.query.page === null ? '1' : route.query.page)

        const { loading, pagination, onRequest } = useAPIPagination(freeFormsFilter, getFreeForms, freeFormsCount, currentPage, true, FormModelCacheTag.FREE_FORMS_LIST_FILTERS)

        function onShowFreeFormRow (evt: PointerEvent | null, props: FreeForm) {
            router.push({ name: FreeFormsRouteNames.SHOW, params: { formId: props.id }, query: { page: route.query.page } })
        }

        const onEnableForm = (props: FreeForm) => {
            if (!props.areTranslationsReady) {
                $q.notify({
                    color: 'negative',
                    message: 'Form can not be enabled, because its translations are not yet ready'
                })
                return
            }
            enableFreeForm(props.id)
            $q.notify({
                color: 'positive',
                message: 'Form has been enabled'
            })
        }

        const onDisableForm = (props: FreeForm) => {
            if (!props.areTranslationsReady) return
            disableFreeForm(props.id)
            $q.notify({
                color: 'positive',
                message: 'Form has been disabled'
            })
        }

        await onRequest({
            filter: props.filter || '',
            pagination: pagination.value
        })

        return {
            freeFormsColumns,
            onShowFreeFormRow,
            onEnableForm,
            onDisableForm,
            loading,
            pagination,
            onRequest
        }
    }
})
